import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, Sparkles, Star, Heart, Bug, Lightbulb, Palette, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const CATEGORIES = [
  { id: "ux",       label: "UX / design",  icon: Palette },
  { id: "feature",  label: "Feature idea", icon: Lightbulb },
  { id: "bug",      label: "Bug / issue",  icon: Bug },
  { id: "content",  label: "Content",      icon: MessageSquare },
  { id: "other",    label: "Other",        icon: Heart },
];

export default function Feedback() {
  const { user } = useAuth();
  const location = useLocation();
  const [form, setForm] = useState({ name: "", email: "", rating: 0, category: "ux", message: "" });
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/feedback/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email }));
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/feedback", {
        ...form,
        rating: form.rating || null,
        email: form.email || null,
        name: form.name || null,
        page: location.pathname,
      });
      toast.success("Thank you — every note helps us build a better home for parents.");
      setForm({ name: form.name, email: form.email, rating: 0, category: form.category, message: "" });
      api.get("/feedback/stats").then((r) => setStats(r.data));
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not send feedback");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="max-w-2xl mb-10">
        <div className="overline mb-3">Your voice</div>
        <h1 data-testid="feedback-heading" className="text-4xl sm:text-5xl font-semibold text-brand-blue leading-tight">
          Tell us what to fix, build, or love more.
        </h1>
        <p className="mt-4 text-lg text-brand-muted">
          FuturePath is built for parents. Every feature you see was suggested by someone before you. Your honest note helps every family that comes next.
        </p>
      </div>

      {stats && stats.total > 0 && (
        <div className="surface p-5 mb-8 flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-brand-green-soft text-brand-green flex items-center justify-center"><MessageSquare size={20} /></div>
            <div>
              <div className="text-xs text-brand-muted">Notes shared by parents</div>
              <div className="font-heading font-semibold text-brand-blue text-lg">{stats.total}</div>
            </div>
          </div>
          {stats.average_rating != null && (
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-brand-orange-soft text-brand-orange flex items-center justify-center"><Star size={20} fill="currentColor" /></div>
              <div>
                <div className="text-xs text-brand-muted">Average rating ({stats.rated_count} ratings)</div>
                <div className="font-heading font-semibold text-brand-blue text-lg">{stats.average_rating} / 5</div>
              </div>
            </div>
          )}
        </div>
      )}

      <form onSubmit={submit} className="surface p-8 lg:p-10 space-y-6">
        <Sparkles size={24} className="text-brand-green" />

        {/* Rating */}
        <div>
          <label className="block text-sm font-medium text-brand-blue mb-3">How is FuturePath treating you? (optional)</label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`feedback-star-${n}`}
                onClick={() => setForm({ ...form, rating: form.rating === n ? 0 : n })}
                className={`w-12 h-12 rounded-2xl border-2 transition-all flex items-center justify-center ${
                  form.rating >= n ? "border-brand-orange bg-brand-orange text-white" : "border-brand-border text-brand-muted hover:border-brand-orange"
                }`}
                aria-label={`${n} stars`}
              >
                <Star size={20} fill={form.rating >= n ? "currentColor" : "none"} />
              </button>
            ))}
          </div>
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-brand-blue mb-3">What's this about?</label>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`feedback-cat-${c.id}`}
                onClick={() => setForm({ ...form, category: c.id })}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border-2 transition-all ${
                  form.category === c.id
                    ? "border-brand-blue bg-brand-blue text-white"
                    : "border-brand-border bg-white text-brand-blue hover:border-brand-blue"
                }`}
              >
                <c.icon size={14} /> {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-brand-blue mb-2">Your message *</label>
          <textarea
            data-testid="feedback-message"
            required minLength={5} rows={6}
            className="field resize-none"
            placeholder="What could we do differently? What's confusing? What would you love to see next?"
            value={form.message}
            onChange={set("message")}
          />
          <div className="mt-1 text-xs text-brand-muted">{form.message.length} / 4000</div>
        </div>

        {/* Contact (optional) */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brand-blue mb-1.5">Name (optional)</label>
            <input data-testid="feedback-name" className="field" value={form.name} onChange={set("name")} />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-blue mb-1.5">Email (only if you'd like a reply)</label>
            <input data-testid="feedback-email" type="email" className="field" value={form.email} onChange={set("email")} />
          </div>
        </div>

        <button data-testid="feedback-submit-btn" type="submit" disabled={loading || form.message.length < 5} className="btn-primary w-full disabled:opacity-60">
          {loading ? (<><Loader2 className="animate-spin" size={14} /> Sending...</>) : "Send feedback"}
        </button>
        <p className="text-xs text-brand-muted text-center">
          We read every message. Personal replies are optional — leave email blank if you'd prefer not.
        </p>
      </form>
    </div>
  );
}
